import { articlesScience } from "@/mocks/articlesScience";
import { articlesPsychology } from "@/mocks/articlesPsychology";
import { articlesBody } from "@/mocks/articlesBody";
import { articlesSelfcare } from "@/mocks/articlesSelfcare";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";

export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface Article {
  id: string;
  title: string;
  summary: string;
  cover: string;
  categoryId: string;
  author: string;
  date: string;
  readCount: number;
  tags: string[];
  sections: ArticleSection[];
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
  accent: string;
}

export interface Topic {
  id: string;
  title: string;
  cover: string;
  intro: string;
  reason: string;
  articleIds: string[];
  tags: string[];
}

export const allArticles: Article[] = [
  ...articlesScience,
  ...articlesPsychology,
  ...articlesBody,
  ...articlesSelfcare,
] as Article[];

export const allCategories: Category[] = categories as Category[];
export const allTopics: Topic[] = topics as Topic[];

export function getCategoryById(id: string): Category | undefined {
  return allCategories.find((item) => item.id === id);
}

export function getArticleById(id: string): Article | undefined {
  return allArticles.find((item) => item.id === id);
}

export function getTopicById(id: string): Topic | undefined {
  return allTopics.find((item) => item.id === id);
}

export function getArticlesByIds(ids: string[]): Article[] {
  return ids
    .map((id) => getArticleById(id))
    .filter((item): item is Article => Boolean(item));
}

export function getArticlesByCategory(categoryId: string): Article[] {
  return allArticles.filter((item) => item.categoryId === categoryId);
}

export function getLatestArticles(limit?: number): Article[] {
  const sorted = [...allArticles].sort((a, b) => (a.date < b.date ? 1 : -1));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getPopularArticles(limit?: number): Article[] {
  const sorted = [...allArticles].sort((a, b) => b.readCount - a.readCount);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getRelatedArticles(article: Article, limit = 4): Article[] {
  const sameCategory = allArticles.filter(
    (item) => item.categoryId === article.categoryId && item.id !== article.id,
  );
  const others = allArticles.filter(
    (item) => item.categoryId !== article.categoryId && item.id !== article.id,
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export function searchArticles(keyword: string): Article[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allArticles.filter((item) => {
    const categoryName = getCategoryById(item.categoryId)?.name ?? "";
    return (
      item.title.toLowerCase().includes(q) ||
      item.summary.toLowerCase().includes(q) ||
      item.tags.join(" ").toLowerCase().includes(q) ||
      categoryName.toLowerCase().includes(q)
    );
  });
}

export function getTopicArticles(topic: Topic): Article[] {
  return getArticlesByIds(topic.articleIds);
}

export function formatDate(value: string): string {
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
}